/* @flow */

import base64url from "base64url";

import { SearchResult } from "shared/src/types/articlesearch/SearchResult";
import { ShoppingListCategory } from "shared/src/types/ShoppingListCategory";
import { Category, CategoryRoot } from "shared/src/types/articlesearch/Category";
import CategoryStore from "../stores/CategoryStore";
import CountryUtil from "shared/src/uidispatcherapi/CountryUtil";
import { cloneDeep, isEmpty } from "shared/src/utils/util";

function categoryFromSearchResult(
    level: number,
    searchResult: SearchResult
): ShoppingListCategory[] | undefined | null {
    const categoryTree = searchResult.categorytree;
    if (!categoryTree) {
        return null;
    }
    const categoriesOfLevel = getCategoriesOfLevel(level, Object.values(categoryTree.children || {}));

    //UNKNOWN category is only on top level, but should also be displayed if level 1 (e.g. tabNavigation enabled)
    //is configured
    const unknownCategory = getUnknownCategory(Object.values(categoryTree.children || {}));
    const categoriesOfLevelWithUnknownCategory =
        unknownCategory && shouldUnknownCategoryBeAdded(level, unknownCategory)
            ? [...categoriesOfLevel, unknownCategory]
            : categoriesOfLevel;

    return (categoriesOfLevelWithUnknownCategory ?? []).map(transformCategoryToShoppingListCategory);
}

function getCategoriesOfLevel(level: number, categories: Category[]): Category[] {
    if (level <= 0) {
        return categories;
    } else {
        return getCategoriesOfLevel(level - 1, getCategories(categories));
    }
}

function getCategories(categories: Category[]): Category[] {
    return (categories ?? []).flatMap((category: Category): Category[] => Object.values(category.children));
}

function getUnknownCategory(categories: Category[]) {
    return categories?.find((cat) => {
        return cat.categoryId === "UNKNOWN";
    });
}

function shouldUnknownCategoryBeAdded(level: number, unknownCategory: Category) {
    return level !== 0 && !isEmpty(unknownCategory);
}

function transformCategoryToShoppingListCategory(category: Category): ShoppingListCategory {
    return {
        name: category.name,
        amounts: category.amounts,
        categoryId: category.categoryId,
        urlCategoryPath: category.urlCategoryPath,
        articles: [],
        articlesToRender: []
    };
}

function drillDownIfUninteresting(category: CategoryRoot | Category): CategoryRoot | Category {
    if (Object.keys(category.children ?? {}).length !== 1) {
        return category;
    }
    const onlyChild = category.children[Object.keys(category.children ?? {})[0]];
    if ("amounts" in category && category.amounts !== onlyChild.amounts) {
        return category;
    }
    return onlyChild;
}

function drillDownToCategory(categories: CategoryRoot, path: string | undefined | null): CategoryRoot | Category {
    if (!path) {
        // if there's just one category that would be shown, drill down
        let category: CategoryRoot | Category = categories;
        while (drillDownIfUninteresting(category) !== category) {
            category = drillDownIfUninteresting(category);
        }
        return category;
    } else {
        let category: CategoryRoot | Category = categories;
        const parts = path.split("/");
        for (let i = 0; i < parts.length; ++i) {
            const pathPrefix = parts.slice(0, i + 1);
            const pathPrefixString = pathPrefix.join("/");
            const nextCategories: Category[] = Object.values(category.children ?? {}).filter(
                (cat: Category) => cat.urlCategoryPath === pathPrefixString
            );
            if (nextCategories.length === 1) {
                category = nextCategories[0];
            } else {
                return category;
            }
        }
        return category;
    }
}

function drillDownToCategoryForSubcategoryPanels(
    categories: CategoryRoot,
    path: string | undefined | null
): CategoryRoot | Category {
    const category: CategoryRoot | Category = drillDownToCategory(categories, path);
    // Since the first category level might just contain possibly uninteresting categories such as
    // `Food` and `Non-Food`, we can configure per country to skip the first level (see MSD-2231).
    if (
        !path &&
        CountryUtil.getOptionalConfig().skipFirstLevelInSubcategoriesPanel &&
        CategoryStore.getCategoryLevel() === 1
    ) {
        const skippedCategory = cloneDeep(category);
        let grandChildren = {};
        for (const child of Object.values(skippedCategory.children)) {
            grandChildren = Object.assign(grandChildren, child.children);
        }
        if (grandChildren) {
            // If grand children are found, move them one level up in the tree, thereby replacing their parents
            skippedCategory.children = grandChildren;
        }
        return skippedCategory;
    }
    return category;
}

function sortCategoriesByAmounts(cats: Category[]): Category[] {
    return [...(cats ?? [])].sort((a, b) => b.amounts - a.amounts);
}

const exported = {
    /**
     * save to use as a CssClass or in urls, human readable.
     */ getCategoryIdentifer(name: string) {
        return "cat_" + base64url(name);
    },

    categoryError: "ERROR",
    categoryFromSearchResult: categoryFromSearchResult,
    getCategoriesOfLevel: getCategoriesOfLevel,
    transformCategoryToShoppingListCategory: transformCategoryToShoppingListCategory,
    drillDownToCategory: drillDownToCategory,
    drillDownToCategoryForSubcategoryPanels: drillDownToCategoryForSubcategoryPanels,
    sortCategoriesByAmounts: sortCategoriesByAmounts
};

export default exported;
export {
    categoryFromSearchResult,
    getCategoriesOfLevel,
    transformCategoryToShoppingListCategory,
    drillDownToCategory,
    drillDownToCategoryForSubcategoryPanels,
    sortCategoriesByAmounts
};

export const { getCategoryIdentifer, categoryError } = exported;
